import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, Empty } from 'antd';
import ChartCard from './ChartCard';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import styled from 'styled-components';

const COLORS = {
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const ChartContainer = styled.div`
  width: 100%;
  height: 350px;
`;

const SelectContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const StyledSelect = styled(Select)`
  width: 150px;
  
  .ant-select-selector {
    background: #161B22 !important;
    border: 1px solid #1f2a37 !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-select-selection-item {
    color: ${COLORS.textPrimary} !important;
  }
`;

interface WalletTrendData {
  date: string;
  count: number;
}

const WalletTrendsChart: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<WalletTrendData[]>([]);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `${API_ENDPOINTS.MERCHANT_GET_WALLET_TRENDS}?days=${days}`
      );
      if (response.data?.status === 'success' && response.data?.data) {
        setData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching wallet trends:', error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#161B22',
          border: '1px solid #1f2a37',
          borderRadius: '8px',
          padding: '12px',
          color: COLORS.textPrimary,
        }}>
          <p style={{ margin: 0, marginBottom: '4px', fontWeight: 600 }}>
            {payload[0].payload.date ? formatDate(payload[0].payload.date) : ''}
          </p>
          <p style={{ margin: 0, color: COLORS.accent }}>
            Wallets: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ChartCard
      title="Wallet Assignment Trends"
      extra={
        <SelectContainer>
          <StyledSelect
            value={days}
            onChange={(value) => setDays(value as number)}
            style={{ width: 150 }}
          >
            <Select.Option value={7}>Last 7 days</Select.Option>
            <Select.Option value={30}>Last 30 days</Select.Option>
            <Select.Option value={90}>Last 90 days</Select.Option>
          </StyledSelect>
        </SelectContainer>
      }
      loading={loading}
    >
      {!loading && data.length === 0 ? (
        <Empty description="No wallet trend data available" />
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2a37" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke={COLORS.textSecondary}
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke={COLORS.textSecondary} style={{ fontSize: '12px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="count"
                stroke={COLORS.accent}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.accent }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </ChartCard>
  );
};

export default WalletTrendsChart;

